import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useMoveActionsMutation } from "@/store/moves/apis";
import { Move } from "@/types";

type CallType = "spoke_to_customer" | "left_voicemail";

const LogCall: React.FC<{
  move: Move;
  triggerChildren: React.ReactNode;
  refetchMove: () => void;
  askForFU?: boolean;
  setFUOpen?: (open: boolean) => void;
}> = ({ move, triggerChildren, refetchMove, setFUOpen }) => {
  const [isLogCallLoading, setIsLogCallLoading] = useState(false);
  const [moveActions] = useMoveActionsMutation();
  const [notes, setNotes] = useState("");
  const [activeCallType, setActiveCallType] = useState<CallType | null>(null);
  const { toast } = useToast();
  const [askIfSetFUReminder, setAskIfSetFUReminder] = useState(false);

  const callTypeConfig = {
    spoke_to_customer: {
      title: "Log call (spoke to customer)",
    },
    left_voicemail: {
      title: "Log call (left voicemail)",
    },
  };

  const saveLogCall = async ({
    event,
    data = {},
    callback,
  }: {
    event: string;
    data?: Record<string, unknown>;
    callback?: () => void;
  }) => {
    const mergedData = {
      outcome: event,
      metadata: data,
    };

    try {
      setIsLogCallLoading(true);
      await moveActions({
        id: move.id,
        action: "log_call",
        data: mergedData,
      }).unwrap();

      toast({
        title: "Success",
        description: "Call logged",
      });
      setIsLogCallLoading(false);

      refetchMove();

      if (callback) {
        callback();
      }
    } catch (err) {
      setIsLogCallLoading(false);
      console.log("err", err);
    }
  };

  const confirmCall = async (callType: CallType) => {
    await saveLogCall({ event: callType, data: { notes } });
    setActiveCallType(null);
    setNotes("");
  };

  return (
    <>
      <div>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>{triggerChildren}</DropdownMenuTrigger>
          <DropdownMenuContent align="center">
            <DropdownMenuItem
              disabled={isLogCallLoading}
              onSelect={() => {
                saveLogCall({ event: "no_answer" });
              }}
            >
              Called - customer did not pick up
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={isLogCallLoading}
              onSelect={() => setActiveCallType("left_voicemail")}
            >
              Called - left voicemail
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={isLogCallLoading}
              onSelect={() => setActiveCallType("spoke_to_customer")}
            >
              Called - spoke to customer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AlertDialog
        open={activeCallType !== null}
        onOpenChange={() => setActiveCallType(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {activeCallType && callTypeConfig[activeCallType].title}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Enter notes here..."
              className="w-full px-3 py-2 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-500 focus:border-blue-500"
            />
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-primary hover:bg-primary/90"
              onClick={() => activeCallType && confirmCall(activeCallType)}
            >
              Log call
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={askIfSetFUReminder}
        onOpenChange={setAskIfSetFUReminder}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Do you want to set a follow-up reminder?
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-primary hover:bg-primary/90"
              onClick={() => setFUOpen && setFUOpen(true)}
            >
              Set a follow-up reminder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default LogCall;
